import graphql__d82757f88f795c73de64 from "./__generated__/MailingListDynamicMembersTableQuery.graphql.ts";import * as React from "react";
import {fetchQuery, graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";

type Props = {
    mailingListId: string;
}

const PAGE_SIZE = 50;

const getContactName = (contact: any): string | null => {
    if (!contact) return null;
    if (contact.teamName) {
        return contact.teamName;
    }
    if (contact.givenName || contact.familyName) {
        return `${contact.givenName || ''} ${contact.familyName || ''}`.trim();
    }
    return contact.emailAddress;
};

/**
 * Loads and renders the current members of a dynamic mailing list (the contacts matching its
 * criterion, minus the unsubscribed ones). Read-only; shows the first page of matches.
 */
const MailingListDynamicMembersTable: React.FC<Props> = ({mailingListId}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const [contacts, setContacts] = React.useState<any[] | null>(null);
    const [hasNextPage, setHasNextPage] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;
        fetchQuery(relayEnvironment, graphql__d82757f88f795c73de64, {id: mailingListId, first: PAGE_SIZE}).toPromise().then((data) => {
            if (cancelled) {
                return;
            }
            //@ts-ignore
            const connection = data?.mailingListDynamicMembers;
            const nodes = (connection?.edges || []).map((edge) => edge?.node).filter(Boolean);
            setContacts(nodes);
            setHasNextPage(connection?.pageInfo?.hasNextPage || false);
        });
        return () => {
            cancelled = true;
        };
    }, [relayEnvironment, mailingListId]);

    if (contacts === null) {
        return (
            <div className="px-6 py-6 text-gray-500 text-center">
                {intl.formatMessage({id: 'mailingList.dynamicMembers.loading'})}
            </div>
        );
    }

    if (contacts.length === 0) {
        return (
            <div className="px-6 py-6 text-gray-500 text-center">
                {intl.formatMessage({id: 'mailingList.noMembers'})}
            </div>
        );
    }

    return (
        <div className="flex-1 min-h-0 relative overflow-y-auto">
            <Table>
                <thead>
                    <tr>
                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.name'})}</TableHeader>
                        <TableHeader>{intl.formatMessage({id: 'list.mailingListMember.header.email'})}</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {contacts.map((contact, index) => (
                        <tr key={contact.id || index} className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group'}>
                            <TableData>
                                <span className="font-medium">{getContactName(contact) || '-'}</span>
                            </TableData>
                            <TableData>
                                {contact.emailAddress || '-'}
                            </TableData>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {hasNextPage && (
                <div className="px-6 py-4 text-gray-500 text-center text-sm">
                    {intl.formatMessage({id: 'mailingList.dynamicMembers.truncated'}, {count: PAGE_SIZE})}
                </div>
            )}
        </div>
    );
};

export default MailingListDynamicMembersTable;
